import React, { useState } from 'react';

interface TermsModalProps {
  onAccept: () => void;
  onDecline: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ onAccept, onDecline }) => {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToBottom(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 mb-1">
            <img src="/logo.png" alt="IndiePress" className="w-7 h-7" />
            <span className="text-gray-900 font-semibold text-sm">IndiePress</span>
          </div>
          <h2 className="text-gray-900 text-lg font-semibold mt-3">Terms & Privacy</h2>
          <p className="text-gray-500 text-sm mt-1">
            Please read and accept our terms before continuing. Scroll to the bottom to enable the Accept button.
          </p>
        </div>

        {/* Scrollable content */}
        <div
          onScroll={handleScroll}
          className="overflow-y-auto px-6 py-5 flex-1 text-sm text-gray-600 space-y-5"
          style={{ lineHeight: 1.7 }}
        >
          <section>
            <h3 className="font-semibold text-gray-900 mb-1">1. Data We Collect</h3>
            <p>
              When you create an account, we collect your name, mobile number, and email address (if provided via Google sign-in).
              We also store the book content, uploaded files, and cover images you submit through the platform.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">2. How We Use Your Data</h3>
            <p>
              Your information is used solely to operate IndiePress — to process your manuscript, generate your book PDF,
              assign an ISBN where applicable, and coordinate printing and delivery. We do not sell your personal data to third parties.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">3. Data Storage</h3>
            <p>
              Your data is stored securely on AWS infrastructure.
              Uploaded files and generated PDFs are stored in encrypted cloud storage. We retain your data for as long as
              your account is active.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">4. Content Ownership</h3>
            <p>
              You retain full ownership of all content you upload or write on IndiePress. By submitting your manuscript
              for publishing, you confirm that you hold the necessary rights to publish it and that it does not infringe
              on any third-party intellectual property.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">5. Communications</h3>
            <p>
              We may send you transactional messages (OTPs, order updates, submission status) via SMS or email.
              By creating an account, you consent to receiving these communications.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">6. Account Deletion</h3>
            <p>
              You may request deletion of your account and associated data at any time by contacting us through Help & support. We will process deletion requests within 30 days.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-1">7. Changes to These Terms</h3>
            <p>
              We may update these terms from time to time. Continued use of IndiePress after changes constitutes
              acceptance of the revised terms. We will notify you of material changes via email or in-app notice.
            </p>
          </section>

          <p className="text-gray-400 text-xs pt-2">
            Operated by Nasadiya Technologies Pvt. Ltd. · Last updated May 2026
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
          <button
            onClick={onAccept}
            disabled={!scrolledToBottom}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-colors disabled:cursor-not-allowed"
            style={{
              background: scrolledToBottom ? '#f97316' : '#e5e7eb',
              color: scrolledToBottom ? '#ffffff' : '#9ca3af',
            }}
          >
            I Accept the Terms & Privacy Policy
          </button>
          <button
            onClick={onDecline}
            className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Decline & go back to login
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
